import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sortOnboardingStandards } from '../utils/sortOnboardingStandards'
import { initialSelectedIdsFromAccepted } from '../utils/onboardingStandardsSelection'
import { MachineryDirectiveOnboardingLayout } from './MachineryDirectiveOnboardingLayout'
import { OnboardingCStandardsReviewStep } from './OnboardingCStandardsReviewStep'
import { OnboardingEPlanStep } from './OnboardingEPlanStep'
import { OnboardingHighRiskStep } from './OnboardingHighRiskStep'
import { OnboardingIntroStep } from './OnboardingIntroStep'
import { OnboardingMachineCharacteristicsExtractionIntroStep } from './OnboardingMachineCharacteristicsExtractionIntroStep'
import { OnboardingMachineCharacteristicsExtractionStep } from './OnboardingMachineCharacteristicsExtractionStep'
import { OnboardingMachineCompletionStep } from './OnboardingMachineCompletionStep'
import { OnboardingMachineLocationsStep } from './OnboardingMachineLocationsStep'
import { OnboardingNoApplicableStandardsStep } from './OnboardingNoApplicableStandardsStep'
import { OnboardingStandardsDiscoveryStep } from './OnboardingStandardsDiscoveryStep'
import { OnboardingStandardsIntroStep } from './OnboardingStandardsIntroStep'
import { OnboardingTypeBQuestionnaireStep } from './OnboardingTypeBQuestionnaireStep'
import { OnboardingTypeCStandardCheckDiscoveryStep } from './OnboardingTypeCStandardCheckDiscoveryStep'
import type { MachineryDirectiveOnboardingProps } from './machineryDirectiveOnboardingTypes'

export type {
  HighRiskCheckDisplay,
  MachineryDirectiveOnboardingProps,
  OnboardingStep,
} from './machineryDirectiveOnboardingTypes'

function isHighRiskOnboardingStep(
  onboardingStep: MachineryDirectiveOnboardingProps['onboardingStep']
): boolean {
  return (
    onboardingStep === 'high_risk' ||
    onboardingStep === 'high_risk_result' ||
    onboardingStep === 'user_high_risk_override_locked'
  )
}

export function MachineryDirectiveOnboarding({
  complianceId,
  productId,
  productName,
  backHref,
  standards,
  standardsDiscoveryCompleted,
  isTypeCReviewCompleted,
  onboardingStep,
  furthestProgressSidebarStepId,
  highRiskCheck,
  highRiskUserOverrideReason,
  userElevatedHighRiskLocked,
  workflowResultIsHighRisk,
  isHighRiskWorkflowRunning,
  highRiskWorkflowError,
  isStandardsWorkflowRunning,
  standardsWorkflowError,
  standardsDiscoveryTaskState,
  isTypeCStandardCheckRunning,
  typeCStandardCheckError,
  typeCStandardCheckTaskState,
  machineCharacteristicsExtractionCompleted,
  isMachineCharacteristicsExtractionBegun,
  isMachineCharacteristicsExtractionRunning,
  machineCharacteristicsExtractionError,
  machineCharacteristicsExtractionTaskState,
  isPartlyCompletedMachine,
  isBackDisabled = false,
  onBack,
  onNavigateToSidebarStep,
  onBeginMachineCharacteristicsExtraction,
  onContinueFromIntro,
  onContinueWithEPlan,
  onContinueWithoutEPlan,
  isTypeBGenerateBlocked,
  onBeginHighRisk,
  onCloseHighRisk,
  onHighRiskClassificationUpdated,
  onContinueFromHighRiskResult,
  onContinueFromMachineCompletion,
  onContinueFromMachineLocations,
  onBeginStandards,
  onContinueFromStandardsReview,
  onRerunTypeCStandardCheck,
  onCancelStandardsDiscovery,
  onCancelTypeCStandardCheck,
}: MachineryDirectiveOnboardingProps) {
  const sortedStandards = useMemo(() => {
    return sortOnboardingStandards(standards)
  }, [standards])

  // Kept on the onboarding shell so Back / sidebar navigation does not discard
  // unsaved Type C/A checkbox edits. A full page refresh remounts and reloads
  // from persisted `isAccepted` on standards.
  const typeCASelectionSeededRef = useRef(standards.length > 0)
  const [typeCASelectedIds, setTypeCASelectedIds] = useState<Set<string>>(
    () => initialSelectedIdsFromAccepted(standards)
  )

  useEffect(() => {
    if (typeCASelectionSeededRef.current) {
      return
    }
    if (standards.length === 0) {
      return
    }
    setTypeCASelectedIds(initialSelectedIdsFromAccepted(standards))
    typeCASelectionSeededRef.current = true
  }, [standards])

  const onTypeCASelectedIdsChange = useCallback(
    (
      next:
        | Set<string>
        | ((previous: Set<string>) => Set<string>)
    ) => {
      setTypeCASelectedIds((previous) => {
        if (typeof next === 'function') {
          return next(previous)
        }
        return next
      })
    },
    []
  )

  const totalCount = sortedStandards.length
  const hasApplicableStandards = totalCount > 0
  const hasNoApplicableStandards = totalCount === 0

  const isIntroStep = onboardingStep === 'intro'
  const isEPlanStep = onboardingStep === 'e_plan'
  const isHighRiskStep = isHighRiskOnboardingStep(onboardingStep)
  const isMachineCompletionStep = onboardingStep === 'machine_completion'
  const isMachineLocationsStep = onboardingStep === 'machine_locations'
  const isStandardsStep = onboardingStep === 'standards'
  const isTypeBQuestionnaireStep = onboardingStep === 'type_b_questionnaire'
  const shouldShowTypeBQuestionnaire =
    isTypeBQuestionnaireStep &&
    machineCharacteristicsExtractionCompleted &&
    !isMachineCharacteristicsExtractionRunning
  const shouldShowMachineCharacteristicsExtractionIntro =
    isTypeBQuestionnaireStep &&
    !machineCharacteristicsExtractionCompleted &&
    !isMachineCharacteristicsExtractionBegun &&
    !isMachineCharacteristicsExtractionRunning
  const shouldShowMachineCharacteristicsExtractionProgress =
    isTypeBQuestionnaireStep &&
    !machineCharacteristicsExtractionCompleted &&
    (isMachineCharacteristicsExtractionBegun ||
      isMachineCharacteristicsExtractionRunning)

  const shouldShowStandardsDiscovery =
    isStandardsStep && isStandardsWorkflowRunning
  const shouldShowStandardsIntro =
    isStandardsStep && hasNoApplicableStandards && !standardsDiscoveryCompleted
  const shouldShowNoApplicableStandards =
    isStandardsStep && hasNoApplicableStandards && standardsDiscoveryCompleted
  const standardsDiscoveryReady =
    standardsDiscoveryCompleted || hasApplicableStandards
  const shouldShowTypeCStandardCheckDiscovery =
    isStandardsStep &&
    hasApplicableStandards &&
    standardsDiscoveryReady &&
    !isTypeCReviewCompleted &&
    isTypeCStandardCheckRunning
  const shouldShowStandardsReview =
    isStandardsStep &&
    hasApplicableStandards &&
    standardsDiscoveryReady &&
    !isTypeCStandardCheckRunning &&
    !isStandardsWorkflowRunning

  const showOnboardingBack = onboardingStep !== 'intro'

  function wrapOnboardingContent(content: React.ReactNode) {
    return (
      <MachineryDirectiveOnboardingLayout
        currentStep={onboardingStep}
        furthestProgressSidebarStepId={furthestProgressSidebarStepId}
        productName={productName ?? null}
        backHref={backHref}
        showOnboardingBack={showOnboardingBack}
        isBackDisabled={isBackDisabled}
        onBack={onBack}
        onNavigateToSidebarStep={onNavigateToSidebarStep}
      >
        {content}
      </MachineryDirectiveOnboardingLayout>
    )
  }

  if (isIntroStep) {
    return wrapOnboardingContent(
      <OnboardingIntroStep onContinue={onContinueFromIntro} />
    )
  }

  if (isEPlanStep) {
    return wrapOnboardingContent(
      <OnboardingEPlanStep
        productId={productId}
        complianceId={complianceId}
        onContinueWithEPlan={onContinueWithEPlan}
        onContinueWithoutEPlan={onContinueWithoutEPlan}
      />
    )
  }

  if (isHighRiskStep) {
    return wrapOnboardingContent(
      <OnboardingHighRiskStep
        complianceId={complianceId}
        onboardingStep={onboardingStep}
        highRiskCheck={highRiskCheck}
        highRiskUserOverrideReason={highRiskUserOverrideReason}
        isUserElevatedHighRiskLocked={userElevatedHighRiskLocked}
        workflowResultIsHighRisk={workflowResultIsHighRisk}
        isHighRiskWorkflowRunning={isHighRiskWorkflowRunning}
        highRiskWorkflowError={highRiskWorkflowError}
        onBeginHighRisk={onBeginHighRisk}
        onCloseHighRisk={onCloseHighRisk}
        onHighRiskClassificationUpdated={onHighRiskClassificationUpdated}
        onContinue={onContinueFromHighRiskResult}
      />
    )
  }

  if (isMachineCompletionStep) {
    return wrapOnboardingContent(
      <OnboardingMachineCompletionStep
        complianceId={complianceId}
        isPartlyCompletedMachine={isPartlyCompletedMachine}
        onContinue={onContinueFromMachineCompletion}
      />
    )
  }

  if (isMachineLocationsStep) {
    return wrapOnboardingContent(
      <OnboardingMachineLocationsStep
        complianceId={complianceId}
        productId={productId}
        onContinue={onContinueFromMachineLocations}
      />
    )
  }

  if (shouldShowStandardsDiscovery) {
    return wrapOnboardingContent(
      <OnboardingStandardsDiscoveryStep
        standardsDiscoveryTaskState={standardsDiscoveryTaskState}
        standardsWorkflowError={standardsWorkflowError}
        onCancel={onCancelStandardsDiscovery}
      />
    )
  }

  if (shouldShowStandardsIntro) {
    return wrapOnboardingContent(
      <OnboardingStandardsIntroStep
        standardsWorkflowError={standardsWorkflowError}
        onBeginStandards={onBeginStandards}
      />
    )
  }

  if (shouldShowNoApplicableStandards) {
    return wrapOnboardingContent(
      <OnboardingNoApplicableStandardsStep onContinue={onContinueFromStandardsReview} />
    )
  }

  if (shouldShowTypeCStandardCheckDiscovery) {
    return wrapOnboardingContent(
      <OnboardingTypeCStandardCheckDiscoveryStep
        typeCStandardCheckTaskState={typeCStandardCheckTaskState}
        typeCStandardCheckError={typeCStandardCheckError}
        onCancel={onCancelTypeCStandardCheck}
      />
    )
  }

  if (shouldShowStandardsReview) {
    return wrapOnboardingContent(
      <OnboardingCStandardsReviewStep
        standards={sortedStandards}
        complianceId={complianceId}
        selectedIds={typeCASelectedIds}
        onSelectedIdsChange={onTypeCASelectedIdsChange}
        onComplete={onContinueFromStandardsReview}
        onRerunTypeCStandardCheck={onRerunTypeCStandardCheck}
        isTypeCStandardCheckRunning={isTypeCStandardCheckRunning}
      />
    )
  }

  if (shouldShowMachineCharacteristicsExtractionIntro) {
    return wrapOnboardingContent(
      <OnboardingMachineCharacteristicsExtractionIntroStep
        extractionError={machineCharacteristicsExtractionError}
        isTypeBGenerateBlocked={isTypeBGenerateBlocked}
        onBeginMachineCharacteristicsExtraction={
          onBeginMachineCharacteristicsExtraction
        }
      />
    )
  }

  if (shouldShowMachineCharacteristicsExtractionProgress) {
    return wrapOnboardingContent(
      <OnboardingMachineCharacteristicsExtractionStep
        extractionTaskState={machineCharacteristicsExtractionTaskState}
        extractionError={machineCharacteristicsExtractionError}
      />
    )
  }

  if (shouldShowTypeBQuestionnaire) {
    return wrapOnboardingContent(
      <OnboardingTypeBQuestionnaireStep
        complianceId={complianceId}
        standards={standards}
        onComplete={onContinueFromStandardsReview}
      />
    )
  }

  return null
}
